<script setup lang="ts">
import { storeToRefs } from 'pinia';

import FoxAndClover from '@clovyr/bed/assets/images/fox-and-clover.png';
import { GridItem, GridSection } from '@clovyr/bed/components/blocks';
import Markdown from '@clovyr/bed/components/blocks/Markdown.vue';
import { ComputeProviderName } from '@clovyr/pollen';

import AppConfiguration from '@/components/forms/AppConfiguration.vue';
import HostSelector from '@/components/forms/HostSelector.vue';
import AppLauncherHero from '@/components/tiers/AppLauncherHero.vue';
import { useLauncherStore } from '@/stores/launcher_store';

import AddonsBackup from './AddonsBackup.vue';
import BYODomain from './BYODomain.vue';

const launcherStore = useLauncherStore();
const { basicSettings, advancedSettings, metadata, selectedHost } = storeToRefs(launcherStore);

const settingsAvailable = computed(
  () => basicSettings.value.length > 0 || advancedSettings.value.length > 0,
);
</script>

<template>
  <div class="review-setup">
    <div class="review-setup__main">
      <AppLauncherHero
        :app-logo="metadata"
        :app-title="metadata.name"
        :right-image-src="FoxAndClover"
        heading-text="Final Review"
      />

      <GridSection :spacing="1" wrap>
        <GridItem :md="6">
          <div class="setup-controls-heading launch-heading--secondary">Hosting On:</div>
          <div class="section-highlighted background-gradient--base selector">
            <HostSelector />
          </div>
        </GridItem>

        <GridItem :md="6" class="config">
          <div v-if="settingsAvailable" class="setup-controls__left grid__col">
            <div class="setup-controls-heading launch-heading--secondary">App Configuration:</div>
            <div class="section-highlighted background-gradient--base">
              <AppConfiguration
                untoggled-button-text="edit settings"
                toggled-button-text="cancel"
                save-button
                :reviewConfigurations="true"
                class="options-block"
              />
            </div>
          </div>
          <div v-else class="section-highlighted background-gradient--base alt-appconfig">
            <markdown :content="metadata.description" />
          </div>
          <div>
            <AddonsBackup
              v-if="selectedHost?.id !== ComputeProviderName.WNext"
              :isReview="true"
              class="addon-backup"
            />
          </div>
          <div>
            <BYODomain class="domain" :bannerStatus="'critical'" />
          </div>
        </GridItem>
      </GridSection>
    </div>
  </div>
</template>

<style scoped lang="scss">
@use '@clovyr/bed/styles/vendor/bootstrap/scss/bootstrap';
@use '@clovyr/bed/styles/core';
@use 'src/styles/core/page';
@use 'src/styles/core/vertical-spacing';
@use 'styles/core/colors';
@use 'styles/core/layout/grid-vars';
@use 'styles/core/typography/type';
@use 'styles/vendor/bootstrap/scss/mixins/breakpoints';

.review-setup__main {
  margin-bottom: 0;

  .alt-appconfig {
    margin-top: grid-vars.space(5.5);
    @include breakpoints.media-breakpoint-down(xs) {
      margin-top: grid-vars.space(1);
    }
  }

  .selector {
    padding: grid-vars.space(3);
  }

  .config {
    margin-bottom: 3rem;
    padding-top: 3rem;

    @include breakpoints.media-breakpoint-up(sm) {
      margin-bottom: 0;
      padding-bottom: 0;
      padding-top: 0;
    }
  }

  .domain {
    :deep(.content-wrapper) {
      font-size: 14px;
    }
  }
  .addon-backup {
    :deep(.checked .slider) {
      border-color: colors.color(grey, primary);
    }
    :deep(.checked .slider:before) {
      color: colors.color(grey, primary);
      border-color: colors.color(grey, primary);
    }
    @include breakpoints.media-breakpoint-up(sm) {
      padding-bottom: 0;
      margin-top: 3rem;
    }
  }
}

.setup-controls-heading {
  @include type.text--h4;
  margin: 0 0 0.5rem 1rem;
  color: colors.color(white);
}
</style>
